import { PLANNER_DEBUG } from '@bridebook/web/lib/ai-planner/debug/format';
import { MAX_ACTIVITY_ENTRIES, type ActivityEntry } from './entry';

// How a registry row keeps its entries.
export type RecordActivityOptions = {
  // The dispatched action's type. Entry limits are per group.
  group: string;
  // Only the latest occurrence matters. A new entry replaces the older ones.
  replacePrevious?: boolean;
  // This group's own entry limit. Past it, the group's oldest entries are dropped.
  maxEntries?: number;
};

/**
 * A buffered entry plus its group, for per-group limits. The group never leaves this
 * module. It is a field, not a summary prefix, because one action type can be a prefix of
 * another (`… field` and `… field success`).
 */
type BufferedEntry = ActivityEntry & { group: string };

// In memory only. The next request posts the entries and the server stores them. Only the
// browser middleware writes here, so it stays empty on the server.
let entries: BufferedEntry[] = [];

/**
 * Records one app action on the rolling log. The chat posts this log with each request,
 * so the coach knows what the couple did outside the chat. Limits are per group, so one
 * group cannot push out another. The total cap stays as a backstop.
 */
export const recordActivity = (
  entry: ActivityEntry,
  { group, replacePrevious = false, maxEntries }: RecordActivityOptions,
) => {
  if (replacePrevious) {
    entries = entries.filter((existing) => existing.group !== group);
  } else {
    const last = entries[entries.length - 1];
    // A repeat of the same event collapses into the previous entry. For example,
    // the couple moves between the same two pages.
    if (last && last.summary === entry.summary) {
      last.at = entry.at;
      return;
    }
  }
  entries.push({ ...entry, group });
  if (maxEntries) {
    // Drop the group's own oldest, leaving every other group untouched.
    let excess = entries.filter((existing) => existing.group === group).length - maxEntries;
    if (excess > 0) {
      entries = entries.filter((existing) => {
        if (excess > 0 && existing.group === group) {
          excess -= 1;
          return false;
        }
        return true;
      });
    }
  }
  if (entries.length > MAX_ACTIVITY_ENTRIES) {
    entries = entries.slice(-MAX_ACTIVITY_ENTRIES);
  }
};

export const getRecentActivity = (): ActivityEntry[] =>
  entries.map(({ at, summary }) => ({ at, summary }));

export const clearActivity = () => {
  entries = [];
};

// Debug: call `__aiPlannerActivity()` in the browser. Uses the same flag as the turn logs,
// so it also works on deployed previews.
if (PLANNER_DEBUG && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__aiPlannerActivity = getRecentActivity;
}
